import React, { useEffect } from 'react';
import { View, StyleSheet, AppState } from 'react-native';
import dayjs from 'dayjs'

import { withSafeArea } from './hoc';
import Navigation from './navigation/';
import { NotificationProvider } from '../src/components/Notification/Notification.provider';
import { loadStoredData, useSessionStore } from './lib/Store/sessionStore.component';
import { navigation } from './lib/Navigation/Navigation.component';
import { isWeb } from '@commonStyle';
import { useFirebaseNotifications } from './hooks/useFirebaseNotifications/useFirebaseNotifications';


require('dayjs/locale/fr')
dayjs.locale('fr')

function useAutoLogout(timeoutMs = 5 * 60 * 1000) {
  if (process.env.NODE_ENV === 'development') {
    timeoutMs = 1000000000;
  }
  const timer = React.useRef<NodeJS.Timeout | null>(null);
  const lastActivity = React.useRef<number>(Date.now());

  const doLogout = React.useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    // Appel impératif au store pour éviter de souscrire aux changements
    await useSessionStore.getState().clearSession();
    !isWeb ? navigation.replace('Connexion') : navigation.navigate('/');
  }, []);

  const resetTimer = React.useCallback(() => {
    lastActivity.current = Date.now();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(doLogout, timeoutMs);
  }, [doLogout, timeoutMs]);

  // Au retour au premier plan : vérifier le temps écoulé depuis la dernière activité
  React.useEffect(() => {
    const sub = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        const elapsed = Date.now() - lastActivity.current;
        const hasToken = !!useSessionStore.getState().session?.access_token;
        if (elapsed >= timeoutMs && hasToken) {
          doLogout();
        } else if (hasToken) {
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(doLogout, timeoutMs - elapsed);
        }
      }
    });
    return () => sub.remove();
  }, [doLogout, timeoutMs]);

  // Sur web : écouter les événements DOM (souris, clavier, scroll)
  // car onTouchStart sur le View racine ne capture que les événements tactiles
  React.useEffect(() => {
    if (!isWeb) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    const handler = () => resetTimer();

    events.forEach(evt => document.addEventListener(evt, handler, { passive: true }));
    return () => {
      events.forEach(evt => document.removeEventListener(evt, handler));
    };
  }, [resetTimer]);

  // Reset timer on mount + cleanup
  React.useEffect(() => {
    resetTimer();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [resetTimer]);

  return resetTimer;
}

const AppContent = () => {
  useFirebaseNotifications();
  return (
    <Navigation />
  );
};

const Content = withSafeArea(AppContent);
export const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV === 'development';
const App = () => {
  // const isDarkMode = useColorScheme() === 'dark';
  const resetInactivityTimer = useAutoLogout();

  // Chargement des données de session pour les actualisations
  useEffect(() => { loadStoredData() }, [])

  return (
    <NotificationProvider
      maxNotifications={5}
      position="top"
    >
      <View style={styles.container} onTouchStart={resetInactivityTimer} onMoveShouldSetResponderCapture={() => { resetInactivityTimer(); return false; }}>
          <Content />
        </View>
    </NotificationProvider>
  );
}

export default App;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
